import { useState } from 'react';
import type { Mode, SwarmRun, SwarmChannel } from '../lib/types';

interface Props {
  run: SwarmRun;
}

const PERSPECTIVE_CHANNELS = ['skeptic', 'realist', 'optimist'];
const DEBATE_CHANNELS = ['solver_r1', 'skeptic', 'solver_r3'];
const DEBATE_LABELS: Record<string, string> = {
  solver_r1: 'Solver R1',
  skeptic: 'Скептик-фактчек',
  solver_r3: 'Solver R3 (финал)',
};

function StateBadge({ state }: { state: SwarmChannel['state'] }) {
  if (state === 'streaming') {
    return (
      <span
        className="inline-block w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse"
        title="streaming"
      />
    );
  }
  if (state === 'done') {
    return (
      <span className="text-green-500 text-xs leading-none" title="done">
        ✓
      </span>
    );
  }
  if (state === 'error') {
    return (
      <span className="text-red-400 text-xs leading-none" title="error">
        ✗
      </span>
    );
  }
  return (
    <span
      className="inline-block w-1.5 h-1.5 rounded-full bg-gray-600"
      title="pending"
    />
  );
}

function ChannelCard({ ch }: { ch: SwarmChannel }) {
  return (
    <div className="bg-gray-900 border border-gray-800 rounded-xl p-3 flex flex-col gap-2 min-h-[200px]">
      <div className="flex items-center justify-between border-b border-gray-800 pb-2">
        <span className="text-sm font-medium text-gray-200">{ch.label}</span>
        <StateBadge state={ch.state} />
      </div>
      {ch.error ? (
        <div className="text-red-400 text-sm">{ch.error}</div>
      ) : (
        <div className="whitespace-pre-wrap text-[14px] leading-relaxed text-gray-100 break-words">
          {ch.text}
          {ch.state === 'streaming' && (
            <span className="inline-block w-1 h-4 bg-gray-400 ml-1 align-middle animate-pulse" />
          )}
        </div>
      )}
    </div>
  );
}

function JudgeCard({ judge }: { judge: SwarmChannel }) {
  return (
    <div className="border-t border-gray-800 pt-3">
      <div className="bg-gray-900 border border-blue-900/40 rounded-xl p-3 flex flex-col gap-2">
        <div className="flex items-center justify-between border-b border-gray-800 pb-2">
          <span className="text-sm font-medium text-blue-300">
            {judge.label}
          </span>
          <StateBadge state={judge.state} />
        </div>
        {judge.error ? (
          <div className="text-red-400 text-sm">{judge.error}</div>
        ) : (
          <div className="whitespace-pre-wrap text-[14px] leading-relaxed text-gray-100 break-words">
            {judge.text}
            {judge.state === 'streaming' && (
              <span className="inline-block w-1 h-4 bg-gray-400 ml-1 align-middle animate-pulse" />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function placeholder(id: string, mode: Mode): SwarmChannel {
  const label =
    mode === 'debate'
      ? (DEBATE_LABELS[id] ?? id)
      : id;
  return {
    channel: id,
    label,
    text: '',
    state: 'pending',
  };
}

function PerspectivesView({ run }: { run: SwarmRun }) {
  const channels = PERSPECTIVE_CHANNELS.map(
    (id) => run.channels[id] ?? placeholder(id, 'perspectives'),
  );
  const [activeTab, setActiveTab] = useState<string>('skeptic');
  return (
    <>
      <div className="md:hidden">
        <div className="flex gap-1 mb-2 overflow-x-auto">
          {channels.map((ch) => {
            const active = activeTab === ch.channel;
            return (
              <button
                key={ch.channel}
                type="button"
                onClick={() => setActiveTab(ch.channel)}
                className={`px-3 py-1.5 rounded-lg text-sm border whitespace-nowrap flex items-center gap-2 ${
                  active
                    ? 'bg-gray-800 border-gray-600 text-white'
                    : 'bg-gray-900 border-gray-800 text-gray-400'
                }`}
              >
                <span>{ch.label}</span>
                <StateBadge state={ch.state} />
              </button>
            );
          })}
        </div>
        {channels
          .filter((ch) => ch.channel === activeTab)
          .map((ch) => (
            <ChannelCard key={ch.channel} ch={ch} />
          ))}
      </div>
      <div className="hidden md:grid md:grid-cols-3 gap-3">
        {channels.map((ch) => (
          <ChannelCard key={ch.channel} ch={ch} />
        ))}
      </div>
    </>
  );
}

function DebateView({ run }: { run: SwarmRun }) {
  const channels = DEBATE_CHANNELS.map(
    (id) => run.channels[id] ?? placeholder(id, 'debate'),
  );
  return (
    <div className="space-y-3">
      {channels.map((ch, i) => (
        <div key={ch.channel} className="flex gap-2 items-start">
          <div className="shrink-0 w-6 text-center text-xs font-mono text-gray-500 pt-3">
            {i + 1}
          </div>
          <div className="flex-1">
            <ChannelCard ch={ch} />
          </div>
        </div>
      ))}
    </div>
  );
}

export function SwarmPanel({ run }: Props) {
  const judge = run.channels['judge'];
  const modeLabel = run.mode === 'debate' ? 'debate' : 'perspectives';
  return (
    <div className="space-y-3">
      {run.account && (
        <div className="text-xs text-gray-500">
          swarm: {modeLabel} · {run.account}
          {run.judge ? ' · judge' : ''}
        </div>
      )}
      {run.mode === 'debate' ? (
        <DebateView run={run} />
      ) : (
        <PerspectivesView run={run} />
      )}
      {judge && <JudgeCard judge={judge} />}
      {run.error && (
        <div className="text-red-400 bg-red-950/40 border border-red-900 p-3 rounded-lg text-sm">
          {run.error}
        </div>
      )}
    </div>
  );
}
